'use client';

/**
 * SectionTabsContext
 *
 * Provides nav-config section tabs from PortalLayout to PageTemplate.
 * PortalLayout resolves the active nav item's tabs and provides them here;
 * PageTemplate consumes them to render the tab bar inside PageContentHeader.
 *
 * @see PR #856 — Navigation & Layout Redesign
 */

import type { NavTab, VisibilityContext } from '@ops-matrix/shared-types';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

export interface SectionTabsContextValue {
  /** Tabs from the active nav item (nav-config section tabs) */
  tabs: NavTab[];
  /** Currently active tab ID */
  activeTab: string;
  /** Callback when tab changes */
  onTabChange: (tabId: string, path: string) => void;
  /** Visibility context for filtering tabs */
  visibilityContext?: VisibilityContext;
}

const SectionTabsContext = createContext<SectionTabsContextValue | null>(null);

export interface SectionTabsProviderProps {
  tabs: NavTab[];
  activeTab: string;
  onTabChange: (tabId: string, path: string) => void;
  visibilityContext?: VisibilityContext;
  children: ReactNode;
}

export function SectionTabsProvider({
  tabs,
  activeTab,
  onTabChange,
  visibilityContext,
  children,
}: SectionTabsProviderProps) {
  const value = useMemo(
    () => ({ tabs, activeTab, onTabChange, visibilityContext }),
    [tabs, activeTab, onTabChange, visibilityContext],
  );

  return <SectionTabsContext.Provider value={value}>{children}</SectionTabsContext.Provider>;
}

/**
 * Returns section tabs context, or null if not inside a SectionTabsProvider.
 * PageTemplate uses this to render nav-config tabs when no page-level tabs are provided.
 */
export function useSectionTabs(): SectionTabsContextValue | null {
  return useContext(SectionTabsContext);
}
